import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import papaparse from 'papaparse';
import ExportOptionsModal from '../components/ExportOptionsModal';
import { reportFields } from '../config/reportFields';
import MessageModal from '../components/MessageModal'; // Import the custom modal

const ReportFilters = ({ filters, setFilters }) => {
    const { startDate, endDate } = filters;
    return (
        <div className="form-section">
            <h3>Filters</h3>
            <div className="form-grid" style={{alignItems: 'flex-end'}}>
                <div className="form-field">
                    <label>Date Range (for Sales)</label>
                    <div style={{display: 'flex', gap: '0.5rem'}}>
                        <DatePicker selected={startDate} onChange={(date) => setFilters(prev => ({...prev, startDate: date}))} selectsStart startDate={startDate} endDate={endDate} placeholderText="Start Date" className="date-picker-input"/>
                        <DatePicker selected={endDate} onChange={(date) => setFilters(prev => ({...prev, endDate: date}))} selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} placeholderText="End Date" className="date-picker-input"/>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ReportingCenter = ({ setView }) => {
    const [activeReport, setActiveReport] = useState('sales');
    const [loading, setLoading] = useState(true);
    const [salesData, setSalesData] = useState([]);
    const [inventoryData, setInventoryData] = useState([]);
    const [productData, setProductData] = useState([]);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [filters, setFilters] = useState({ startDate: null, endDate: null });
    const [modalState, setModalState] = useState({ message: '' }); // State for the custom modal

    useEffect(() => {
        setLoading(true);
        const salesQuery = query(collection(db, "sales"), orderBy("saleDate", "desc"));
        const invQuery = query(collection(db, "inventoryItems"), orderBy("childId", "asc"));
        const prodQuery = query(collection(db, "products"), orderBy("createdAt", "desc"));

        const unsubSales = onSnapshot(salesQuery, (snap) => setSalesData(snap.docs.map(d => ({...d.data(), id: d.id, saleDate: d.data().saleDate.toDate() }))));
        const unsubInv = onSnapshot(invQuery, (snap) => setInventoryData(snap.docs.map(d => ({...d.data(), id: d.id, createdAt: d.data().createdAt?.toDate() }))));
        const unsubProd = onSnapshot(prodQuery, (snap) => setProductData(snap.docs.map(d => ({...d.data(), id: d.id, createdAt: d.data().createdAt?.toDate() }))));
        
        setTimeout(() => setLoading(false), 2000); 
        return () => { unsubSales(); unsubInv(); unsubProd(); };
    }, []);
    
    const combinedProductData = useMemo(() => {
        const parentMap = new Map(productData.map(p => [p.id, p]));
        return inventoryData.map(child => {
            const parent = parentMap.get(child.parentDocId);
            return parent ? {
                productId: parent.productId, parentItemName: parent.itemName, parentCategory: parent.category,
                parentTotalItems: parent.totalItems, parentPerUnitCostMYR: parent.perUnitCostMYR,
                childId: child.childId, childItemName: child.itemName, childStatus: child.status,
                childSize: child.size, childColor: child.color, childPerUnitCostMYR: child.perUnitCostMYR,
                childSellingPriceMYR: child.sellingPriceMYR, childProfitMYR: child.profitMYR
            } : null;
        }).filter(Boolean);
    }, [productData, inventoryData]);

    const filteredData = useMemo(() => {
        let data = [];
        if (activeReport === 'sales') data = salesData;
        if (activeReport === 'inventory') data = inventoryData;
        if (activeReport === 'product_overview') data = combinedProductData;
        
        const { startDate, endDate } = filters;
        if (!startDate || !endDate) return data;

        const adjustedEndDate = new Date(endDate);
        adjustedEndDate.setHours(23, 59, 59, 999);
        
        return data.filter(d => {
            const itemDate = activeReport === 'sales' ? d.saleDate : d.createdAt;
            return itemDate && itemDate >= startDate && itemDate <= adjustedEndDate;
        });
    }, [activeReport, filters, salesData, inventoryData, combinedProductData]);

    const handleExportClick = () => {
        if (filteredData.length === 0) {
            // Replaced alert() with the custom modal
            setModalState({ message: "No data available to export for the selected filters." });
            return;
        }
        setIsExportModalOpen(true);
    };

    const handleConfirmExport = (selectedKeys) => { /* ... Unchanged ... */ };

    const renderReport = () => {
        if (loading) return <p>Loading report data...</p>;
        
        const reportTable = (headers, rows) => (
            <div className="table-wrapper">
                <table className="product-list-table">
                    <thead><tr>{headers.map(h => <th key={h.key} className={h.className}>{h.label}</th>)}</tr></thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr><td colSpan={headers.length} style={{textAlign: 'center'}}>No data for the selected period.</td></tr>
                        ) : (
                            rows.map((d, i) => (
                                <tr key={d.id || d.childId || i}>{headers.map(h => <td key={h.key} className={h.className}>{h.render(d)}</td>)}</tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        );

        if (activeReport === 'sales') { /* ... Unchanged ... */ }
        if (activeReport === 'inventory') { /* ... Unchanged ... */ }
        
        if (activeReport === 'product_overview') {
            const headers = [
                { key: 'productId', label: 'Parent ID', render: (d) => d.productId },
                { key: 'parentItemName', label: 'Parent Name', render: (d) => d.parentItemName },
                { key: 'childId', label: 'Child ID', render: (d) => d.childId },
                { key: 'childItemName', label: 'Child Name', render: (d) => d.childItemName },
                { key: 'childStatus', label: 'Status', render: (d) => d.childStatus },
                { key: 'childCost', label: 'Child Cost', className: 'cell-center', render: (d) => Number(d.childPerUnitCostMYR || 0).toFixed(2) },
                { key: 'childPrice', label: 'Child Price', className: 'cell-center', render: (d) => Number(d.childSellingPriceMYR || 0).toFixed(2) },
            ];
            return reportTable(headers, filteredData);
        }
        
        if (activeReport === 'pnl') { /* ... Unchanged ... */ }
        return <p>Select a report to view.</p>;
    };

    return (
        <div>
            <MessageModal message={modalState.message} onConfirm={() => setModalState({ message: '' })} />
            <ExportOptionsModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onConfirmExport={handleConfirmExport} availableFields={reportFields[activeReport] || []}/>
            <div className="page-header">
                <h2>Reporting Center</h2>
                <div style={{display: 'flex', gap: '1rem'}}>
                    <button className="submit-button" onClick={handleExportClick}>Export to Excel</button>
                    <button className="cancel-button" onClick={() => setView('companyDashboard')}>Back</button>
                </div>
            </div>
            <div className="main-nav" style={{justifyContent: 'flex-start', borderTop: 'none', paddingLeft: 0}}>
                <button onClick={() => setActiveReport('sales')} className={activeReport === 'sales' ? 'active' : ''}>Sales Report</button>
                <button onClick={() => setActiveReport('inventory')} className={activeReport === 'inventory' ? 'active' : ''}>Inventory Report</button>
                <button onClick={() => setActiveReport('product_overview')} className={activeReport === 'product_overview' ? 'active' : ''}>Product Overview</button>
                <button onClick={() => setActiveReport('pnl')} className={activeReport === 'pnl' ? 'active' : ''}>Profit & Loss</button>
            </div>
            {activeReport !== 'pnl' && <ReportFilters filters={filters} setFilters={setFilters} />}
            <div style={{marginTop: '1.5rem'}}>
                {renderReport()}
            </div>
        </div>
    );
};

export default ReportingCenter;